import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, HelpCircle, Loader2, Network, Radar, RefreshCw, Sparkles } from 'lucide-react';
import NetworkMapCanvas from './NetworkMapCanvas';
import MapMethodModal from './MapMethodModal';
import { monitorAPI } from '../../services/api';
import { useUrlFlag, useUrlPatch, useUrlState } from '../../hooks/useUrlState';
import { useAuth } from '../../contexts/AuthContext';

const HIGHLIGHT_COLOR = {
  eol: { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  pivot: { text: 'text-shinobi-purple', bg: 'bg-shinobi-purple/5', border: 'border-shinobi-purple/25' },
  risk: { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
  scale: { text: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
};

// "Ask Shinobi AI about this network" bar: cached AI narrative of the vault's
// topology (EOL clusters, pivots, riskiest hosts, scale). Mirrors the
// AIRiskTriage pattern in AssetInventory.js. Operator-only generate button;
// Wasabian sees a cached narrative (if any) but cannot (re)generate one.
function TopologyNarration({ vault }) {
  const { isOperator } = useAuth();
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const aliveRef = useRef(true);
  const pollRef = useRef(null);
  // The vault whose data we currently want on screen. aliveRef alone is not
  // enough: it is flipped back to true by the next effect run, so a slow
  // response for the previous vault would otherwise still be applied.
  const wantedVaultRef = useRef(vault);

  const load = useCallback(async () => {
    const fresh = () => aliveRef.current && wantedVaultRef.current === vault;
    try {
      const resp = await monitorAPI.getTopologyNarrative(vault);
      if (!fresh()) return null;
      setNarrative(resp.data);
      setError(null);
      setGenerating(resp.data.status === 'generating');
      return resp.data;
    } catch (err) {
      if (!fresh()) return null;
      setError(err?.response?.data?.detail || err.message);
      return null;
    } finally {
      if (fresh()) setLoading(false);
    }
  }, [vault]);

  // Re-fetch the cached narrative whenever the vault changes, and stop any
  // in-flight poll from the previous vault so it never sets state for the
  // wrong vault (or after unmount).
  useEffect(() => {
    aliveRef.current = true;
    wantedVaultRef.current = vault;
    setLoading(true);
    setNarrative(null);
    setError(null);
    setGenerating(false);
    load();
    return () => {
      aliveRef.current = false;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [load, vault]);

  useEffect(() => {
    if (!generating) return undefined;
    pollRef.current = setInterval(async () => {
      const d = await load();
      if (!aliveRef.current) return;
      if (d && d.status !== 'generating') {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 4000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [generating, load]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      await monitorAPI.narrateTopology(vault);
      await load();
    } catch (err) {
      if (!aliveRef.current || wantedVaultRef.current !== vault) return;
      setError(err?.response?.data?.detail || err.message);
      setGenerating(false);
    }
  };

  const status = narrative?.status;
  const hasResult = status === 'ok' && !!narrative?.summary;
  const highlights = narrative?.highlights || [];
  // Also gated on !loading: clicking while the initial GET is still in flight
  // would let that older response flip `generating` back off.
  const canGenerate = isOperator && !generating && !loading;

  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-shinobi-purple/5 to-transparent">
      <div className="w-8 h-8 rounded-lg border border-shinobi-purple/25 bg-shinobi-purple/10 flex items-center justify-center text-shinobi-purple shrink-0">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{narrative?.model_label || 'Wasabi Shinobi AI'}</span>
          {hasResult && narrative?.generated_at && (
            <span className="text-[10px] text-slate-400" title={narrative.generated_at}>
              narrated {new Date(narrative.generated_at).toLocaleString()}
            </span>
          )}
          {generating && <Loader2 className="w-3.5 h-3.5 animate-spin text-shinobi-purple" />}
          {canGenerate && (
            <button
              onClick={generate}
              className="inline-flex items-center gap-1.5 ml-auto px-2.5 py-1 text-[11px] rounded border border-shinobi-purple/30 bg-shinobi-purple/10 text-shinobi-purple hover:bg-shinobi-purple/20 transition-colors"
            >
              {hasResult ? <RefreshCw className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              {hasResult ? 'Refresh narration' : 'Ask Wasabi Shinobi AI about this network'}
            </button>
          )}
        </div>
        {loading ? (
          <p className="text-xs text-slate-500 mt-1">Loading narration...</p>
        ) : generating ? (
          <p className="text-xs text-slate-500 mt-1">Wasabi Shinobi AI is reading this network's topology...</p>
        ) : status === 'error' ? (
          <p className="text-xs text-red-400 mt-1">Narration failed: {narrative?.error || 'unknown error'}</p>
        ) : hasResult ? (
          <>
            <p className="text-xs text-slate-700 mt-1 leading-relaxed">{narrative.summary}</p>
            {highlights.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {highlights.map((h, i) => {
                  const c = HIGHLIGHT_COLOR[h.kind] || HIGHLIGHT_COLOR.scale;
                  return (
                    <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full border ${c.bg} ${c.text} ${c.border}`}>
                      {h.text}
                    </span>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-slate-500 mt-1">
            {isOperator
              ? 'Get an AI narration of this network: EOL clusters, pivots, and the riskiest hosts.'
              : 'No AI narration has been generated for this vault yet.'}
          </p>
        )}
        {error && !generating && status !== 'error' && (
          <p className="text-[11px] text-red-400 mt-1">{error}</p>
        )}
      </div>
    </div>
  );
}


const RISK_TONE = {
  critical: 'text-red-700 bg-red-50 border-red-200',
  high: 'text-red-700 bg-red-50 border-red-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low: 'text-slate-600 bg-slate-50 border-slate-200',
  info: 'text-slate-600 bg-slate-50 border-slate-200',
  none: 'text-shinobi-green bg-wasabi-50 border-wasabi-200',
};

// What a host is, and what it touches beyond its own segment.
//
// The estate view exists to answer how segments relate, so the useful thing to
// say about a single dot here is not only what it is but whether it bridges
// anything. Reachability is reported as measured or not measured; it is never
// implied from one host to another across a boundary, because that is not what
// a beacon probe measures.
function HostCard({ hover, board }) {
  if (!hover) return null;
  const a = hover.asset;
  const risk = a.risk || 'none';
  const lateral = a.lateral_ports || [];
  // Keep the card inside the board rather than letting it hang off the edge.
  const flipX = board.w && hover.x > board.w - 260;
  // A bridge runs sideways to the twin segment, and the card was landing on top
  // of the line it is explaining. Drop below the host instead.
  const bridged = !!a.multi_homed;
  const flipY = bridged
    ? board.h && hover.y > board.h - 300
    : board.h && hover.y > board.h - 190;
  return (
    <div
      className="absolute z-20 pointer-events-none w-[248px] rounded-xl border border-slate-300 shadow-2xl p-3 map-fade-in"
      // Opaque, not a tint: the card sits over the board and anything showing
      // through it competes with the text it exists to make readable.
      style={{
        background: '#ffffff',
        left: flipX ? undefined : Math.max(8, hover.x + 16),
        right: flipX ? Math.max(8, board.w - hover.x + 16) : undefined,
        top: flipY ? undefined : Math.max(8, hover.y + (bridged ? 22 : -12)),
        bottom: flipY ? Math.max(8, board.h - hover.y + (bridged ? 22 : -12)) : undefined,
      }}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-[13px] font-semibold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{a.ip}</span>
        <span className={`ml-auto text-[9.5px] px-1.5 py-0.5 rounded-full border ${RISK_TONE[risk] || RISK_TONE.none}`}>
          {risk === 'none' ? 'no findings' : risk}
        </span>
      </div>
      {a.hostname && <div className="text-[11px] text-slate-500 truncate">{a.hostname}</div>}
      <div className="mt-2 space-y-1 text-[11px] text-slate-600">
        {a.role && <Row k="Role" v={a.role} />}
        {a.os_name && (
          <Row
            k="OS"
            v={`${a.os_name} ${a.os_version || ''}`.trim()}
            tone={a.eol_status === 'eol' ? 'text-red-600' : a.eol_status === 'approaching' ? 'text-amber-600' : ''}
            suffix={a.eol_status === 'eol' ? ' (EOL)' : a.eol_status === 'approaching' ? ' (EOL soon)' : ''}
          />
        )}
        <Row k="Subnet" v={a.subnet} mono />
        <Row k="Open ports" v={a.open_ports || 'none'} />
        {a.worst_cve && <Row k="Worst CVE" v={a.worst_cve} mono />}
      </div>

      {lateral.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-shinobi-purple mb-1">Movable through</div>
          <div className="flex flex-wrap gap-1">
            {lateral.map((p) => (
              <span key={p} className="text-[9.5px] px-1.5 py-0.5 rounded-full border border-shinobi-purple/25 bg-shinobi-purple/5 text-shinobi-purple">
                {LATERAL_LABEL[p] || p}
              </span>
            ))}
          </div>
          {LATERAL_GRANT[lateral[0]] && (
            <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
              {LATERAL_LABEL[lateral[0]] || lateral[0]} gives {LATERAL_GRANT[lateral[0]]}.
            </p>
          )}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-slate-100 text-[10.5px] leading-relaxed">
        {a.multi_homed ? (
          <p className="text-shinobi-purple font-medium">
            Bridges two segments under one hardware address. The dashed line shows where it comes
            out on the other side.
          </p>
        ) : a.reach_measured ? (
          <p className="text-slate-600">
            {a.reach_open_ports?.length
              ? `Answered on ${a.reach_open_ports.join(', ')} when probed.`
              : 'Probed, and nothing answered.'}
            {a.reach_disputed && ' Vantage points disagree, so something filters by source.'}
          </p>
        ) : (
          <p className="text-slate-400">
            Nothing has probed this host, so what it accepts is unmeasured rather than closed.
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, mono, tone, suffix }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-400 w-[62px] shrink-0">{k}</span>
      <span className={`truncate ${tone || 'text-slate-700'}`} style={mono ? { fontFamily: 'JetBrains Mono, monospace' } : undefined}>
        {v}{suffix}
      </span>
    </div>
  );
}

const LATERAL_LABEL = {
  22: 'SSH', 445: 'SMB', 3389: 'RDP', 5985: 'WinRM',
  5432: 'Postgres', 3306: 'MySQL', 1433: 'MSSQL', 6379: 'Redis',
};

// What each service hands whoever reaches it. "Pivotable" is a claim, and the
// card should say why rather than expect the reader to take it on trust.
const LATERAL_GRANT = {
  22: 'shell access, and credentials that often work elsewhere',
  445: 'file shares and remote execution',
  3389: 'interactive logon to the desktop',
  5985: 'remote PowerShell',
  5432: 'direct data access, and stored credentials',
  3306: 'direct data access, and stored credentials',
  1433: 'direct data access, and command execution',
  6379: 'unauthenticated key access on most default builds',
};

const LENSES = [['risk', 'Risk'], ['eol', 'EOL'], ['criticality', 'AI criticality']];

// Off leads because it is the board people already know, and because a mesh is
// something you ask for rather than something that should greet you.
const MESH_VIEWS = [
  ['off', 'Off', 'The plain board. Hover a host to fan out what it reaches.'],
  ['lateral', 'Lateral', 'Only the hosts running a service you can move through, meshed. One ring per segment.'],
  ['full', 'Full', 'Every pair counted into groups instead of drawn. Click a group to open it.'],
];

export default function NetworkMap({ vault, onOpenAsset, liveStatus }) {
  const [topo, setTopo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Lens, mesh, drill-down and the opened group all live in the URL: these are
  // the "2 or 3 steps" a refresh used to throw away.
  const [mode, setMode] = useUrlState('lens', 'risk');
  const [pivotsOn, setPivotsOn] = useUrlFlag('pivots');
  // level 0 is the estate, level 1 is one subnet expanded. A vault with a
  // single subnet has no estate worth showing, so it opens already drilled in.
  // Read-only here: every writer of these goes through patchUrl below, because
  // they all change more than one key at a time.
  const [focusParam] = useUrlState('seg', '');
  const focus = focusParam || null;
  // A single-subnet vault auto-focuses, so leaving that level needs an explicit
  // flag: clearing `focus` alone lets the auto-focus immediately re-apply and
  // the estate becomes unreachable.
  // A single-subnet vault auto-focuses, so "I deliberately stepped back out to
  // all subnets" is a state of its own and has to survive a refresh too.
  const [atEstate] = useUrlFlag('estate');
  // Mesh view over the same hosts. Off is the board as it has always been;
  // lateral and full are two ways of drawing what a flat segment implies.
  const [meshView] = useUrlState('mesh', 'off');
  const patchUrl = useUrlPatch();
  const [expandedParam, setExpandedParam] = useUrlState('group', '');
  const expanded = expandedParam || null;
  const setExpanded = useCallback((v) => setExpandedParam(v || ''), [setExpandedParam]);
  const [methodOpen, setMethodOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const [board, setBoard] = useState({ w: 0, h: 0 });
  const boardRef = useRef(null);
  const reqRef = useRef(0);

  const fetchTopo = useCallback(async () => {
    const token = ++reqRef.current;
    try {
      const resp = await monitorAPI.getTopology(vault);
      if (reqRef.current !== token) return;
      setTopo(resp.data); setError(null);
    } catch (err) {
      if (reqRef.current !== token) return;
      setError(err?.response?.data?.detail || err.message);
    } finally {
      if (reqRef.current === token) setLoading(false);
    }
  }, [vault]);

  useEffect(() => { fetchTopo(); return () => { reqRef.current += 1; }; }, [fetchTopo]);
  // Reset the view when the vault changes, but not on first mount: mounting is
  // where the URL's restored state arrives, and clearing it there would undo
  // the whole point of putting it in the URL.
  const seenVaultRef = useRef(vault);
  useEffect(() => {
    if (seenVaultRef.current === vault) return;
    seenVaultRef.current = vault;
    patchUrl({ seg: null, estate: null, mesh: null, group: null });
  }, [vault, patchUrl]);

  const discovering = !!(liveStatus?.monitor_status?.discovering);
  useEffect(() => {
    if (!discovering) return undefined;
    const id = setInterval(fetchTopo, 10000);
    return () => clearInterval(id);
  }, [discovering, fetchTopo]);

  const subnets = topo?.subnets || [];
  const single = subnets.length === 1 ? subnets[0].cidr : null;
  const effFocus = atEstate ? focus : (focus || single);
  const level = effFocus ? 1 : 0;

  // Each of these changes more than one URL key, so they go through one patch:
  // separate setters in the same handler overwrite each other.
  const drill = useCallback((cidr) => {
    patchUrl({ estate: null, seg: cidr, group: null });
  }, [patchUrl]);
  const up = useCallback(() => {
    patchUrl({ estate: '1', seg: null, group: null });
  }, [patchUrl]);
  const setMesh = useCallback((m) => {
    patchUrl({ mesh: m === 'off' ? null : m, group: null });
  }, [patchUrl]);

  if (loading) return <div className="cyber-card p-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building map...</div>;
  if (error) return <div className="cyber-card p-8 text-red-600 text-sm">Error: {error}</div>;
  if (!topo || topo.assets.length === 0) return <div className="cyber-card p-10 text-center text-slate-500 text-sm">No assets to map yet for this vault.</div>;

  const intel = topo.intel;
  const focusSub = effFocus ? subnets.find((s) => s.cidr === effFocus) : null;
  const rail = level === 0
    ? [['assets', 'Assets', intel.assets, ''], ['subnets', 'Subnets', intel.subnets, ''],
       ['high_risk', 'High risk', intel.high_risk, 'text-red-600'], ['eol', 'EOL', intel.eol, 'text-amber-600'],
       ['pivots', 'Pivots', intel.pivots, 'text-shinobi-purple'], ['vulnerable', 'Vulnerable', intel.vulnerable, '']]
    : [['assets', 'Hosts', focusSub?.asset_count ?? 0, ''],
       ['crit', 'Critical', focusSub?.risk_counts?.critical ?? 0, 'text-red-600'],
       ['high', 'High', focusSub?.risk_counts?.high ?? 0, 'text-red-600'],
       ['med', 'Medium', focusSub?.risk_counts?.medium ?? 0, 'text-amber-600'],
       ['eol', 'EOL', focusSub?.eol_count ?? 0, 'text-amber-600'],
       ['subnets', 'Subnets', intel.subnets, '']];

  return (
    <div className="cyber-card overflow-hidden">
      <TopologyNarration vault={vault} />

      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">
          <Radar className="w-3.5 h-3.5" /> Network map
        </span>
        {level === 1 && (
          <button onClick={up} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
            <ChevronLeft className="w-3 h-3" /> All subnets
          </button>
        )}
        {level === 1 && (
          <span className="text-[11px] text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{effFocus}</span>
        )}
        {discovering && <span className="inline-flex items-center gap-1 text-[11px] text-shinobi-green"><span className="w-1.5 h-1.5 rounded-full bg-shinobi-green animate-pulse" /> Discovering</span>}
        <div className="ml-auto flex items-center gap-1.5">
          <div role="tablist" aria-label="Mesh view" className="inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 mr-1">
            {MESH_VIEWS.map(([k, label, hint]) => (
              <button
                key={k}
                role="tab"
                aria-selected={meshView === k}
                title={hint}
                onClick={() => setMesh(k)}
                className={`text-[11px] px-2.5 py-1 rounded-md transition-all ${meshView === k ? 'bg-white text-wasabi-700 font-semibold shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {LENSES.map(([k, label]) => (
            <button key={k} onClick={() => setMode(k)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${mode === k ? 'bg-wasabi-100 text-wasabi-700 border-wasabi-300 font-semibold' : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700'}`}>{label}</button>
          ))}
          <button
            onClick={() => setPivotsOn((v) => !v)}
            disabled={!intel.pivots}
            title={intel.pivots
              ? `Highlight the ${intel.pivots} host${intel.pivots === 1 ? '' : 's'} whose hardware address appears in two subnets`
              : 'No host in this vault appears in two subnets, so there is nothing to highlight. Multi-homed hosts need more than one segment to bridge.'}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${pivotsOn ? 'bg-shinobi-purple/10 text-shinobi-purple border-shinobi-purple/30 font-semibold' : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700'} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500`}
          >
            Pivots{intel.pivots ? ` ${intel.pivots}` : ''}
          </button>
          <button
            onClick={() => setMethodOpen(true)}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors"
            title="What the beacons actually do to produce this picture"
          >
            <HelpCircle className="w-3.5 h-3.5" /> How this is built
          </button>
          <button onClick={fetchTopo} className="text-slate-400 hover:text-slate-700 p-1" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-[128px_1fr]">
        <div className="p-3 border-r border-slate-100 bg-white">
          <div className="text-[8px] tracking-[0.2em] font-bold text-shinobi-green mb-2">NETWORK INTEL</div>
          {rail.map(([k, label, v, cls]) => (
            <div key={k} className="flex justify-between items-baseline py-1">
              <span className={`text-[15px] font-extrabold ${cls}`}>{v}</span>
              <span className="text-[8px] uppercase text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div
          className="relative"
          ref={(elx) => {
            boardRef.current = elx;
            if (elx) {
              const r = elx.getBoundingClientRect();
              if (r.width && (r.width !== board.w || r.height !== board.h)) {
                setBoard({ w: r.width, h: r.height });
              }
            }
          }}
        >
          <HostCard hover={hover} board={board} />
          <NetworkMapCanvas
            topo={topo}
            mode={mode}
            pivotsOn={pivotsOn}
            level={level}
            focus={effFocus}
            mesh={meshView}
            expanded={expanded}
            onDrill={drill}
            onExpand={setExpanded}
            onHostHover={setHover}
            onOpenAsset={onOpenAsset}
          />
          <div className="absolute left-4 bottom-3 text-[10px] text-slate-400 pointer-events-none">
            {meshView === 'full'
              ? 'Click a group to open it'
              : meshView === 'lateral'
                ? 'Hover a host to isolate its pivot paths'
                : level === 0
                  ? 'Hover any host for detail, click to open it, or click a subnet to drill in'
                  : 'Hover a host to fan out what it reaches, click for detail'}
          </div>
        </div>
      </div>

      <MeshPanel topo={topo} level={level} focus={effFocus} />

      <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-200 text-[10px] text-slate-500 flex-wrap">
        {mode === 'eol'
          ? <><Legend c="#00ce3e" l="OK" /><Legend c="#f59e0b" l="Approaching" /><Legend c="#ef4444" l="EOL" /><Legend c="#94a3b8" l="Unknown" /></>
          : mode === 'criticality'
            ? <><Legend c="#ef4444" l="Critical/High" /><Legend c="#f59e0b" l="Medium" /><Legend c="#00ce3e" l="Low" /><Legend c="#94a3b8" l="Unknown" /></>
            : <><Legend c="#00ce3e" l="Healthy" /><Legend c="#f59e0b" l="Medium" /><Legend c="#ef4444" l="High/Critical" /></>}
        <Legend c="#7c4ad0" l="Pivot (multi-homed)" />
        <span className="inline-flex items-center gap-1.5">
          <svg width="14" height="14"><circle cx="7" cy="7" r="3.2" fill="#94a3b8" /><circle cx="7" cy="7" r="5.4" fill="none" stroke="#d97706" strokeWidth="1.4" /></svg>
          Amber ring = EOL host
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="14" height="14"><circle cx="7" cy="7" r="5.5" fill="#ffffff" stroke="#94a3b8" strokeWidth="0.8" /><text x="7" y="9.6" textAnchor="middle" style={{ fontSize: 7, fontWeight: 700, fill: '#475569' }}>3</text></svg>
          Open ports
        </span>
        {topo.reachability?.disputed_hosts > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <svg width="16" height="16"><circle cx="8" cy="8" r="3" fill="#94a3b8" /><circle cx="8" cy="8" r="6" fill="none" stroke="#7c4ad0" strokeWidth="1.3" strokeDasharray="3 3" /></svg>
            Dashed ring = beacons disagree, filtered by source
          </span>
        )}
        <LegendLine c="#334155" dash={false} l="Gateway" />
        <LegendLine c="#64748b" dash l="Router/hop" />
      </div>

      <MapMethodModal open={methodOpen} onClose={() => setMethodOpen(false)} />
    </div>
  );
}

// What a flat segment implies, stated rather than drawn.
//
// A /24 is one broadcast domain: there is no boundary inside it to draw edges
// across, and drawing a line per host pair would be 2450 lines that assert
// nothing. So the panel says the implication in words, and then reports what
// was actually measured, keeping the two clearly apart.
function MeshPanel({ topo, level, focus }) {
  const reach = topo?.reachability || {};
  const segs = reach.subnets || [];

  if (level === 0) {
    const cross = (topo?.edges || []).length;
    return (
      <div className="flex items-start gap-2 px-4 py-2.5 border-t border-slate-200 bg-white text-[11px] text-slate-600">
        <Network className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
        <span>
          {(topo?.subnets || []).length} segment{(topo?.subnets || []).length === 1 ? '' : 's'} and{' '}
          {cross} routed link{cross === 1 ? '' : 's'} between them: gateways, traceroute hops and
          multi-homed pivots the beacons reported. Traffic between segments has to cross one of
          those links.{' '}
          {reach.measured
            ? `Reachability measured on ${reach.measured_hosts} host${reach.measured_hosts === 1 ? '' : 's'} from ${reach.vantage_points} vantage point${reach.vantage_points === 1 ? '' : 's'}.`
            : 'Host to host reachability has not been probed, so the absence of a line means nobody tested that path, not that it is closed.'}
          {' '}Open a segment to see what its hosts accept.
        </span>
      </div>
    );
  }

  const seg = segs.find((s) => s.cidr === focus);
  // Fall back to the topology's own count: the structural statement below is
  // true whether or not anything has been probed, so it must not read "0
  // hosts" just because the reachability block is absent.
  const hosts = seg?.hosts
    ?? (topo?.subnets || []).find((s) => s.cidr === focus)?.asset_count
    ?? 0;
  const paths = hosts > 1 ? hosts * (hosts - 1) : 0;
  const measured = (seg?.measured_hosts ?? 0) > 0;

  return (
    <div className="flex items-start gap-2 px-4 py-2.5 border-t border-slate-200 bg-white text-[11px] text-slate-600">
      <Network className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
      <div className="space-y-1">
        <p>
          <span className="font-mono text-slate-700">{focus}</span> is one broadcast domain. Nothing
          inside it routes or filters, so any of its {hosts} host{hosts === 1 ? '' : 's'} can open a
          connection to any other, {paths.toLocaleString()} possible path
          {paths === 1 ? '' : 's'} in all. What each host accepts is decided by that host alone,
          which is why the map draws no lines here.
        </p>
        {!measured ? (
          <p className="text-amber-700">
            None of those paths has been probed. Run Probe reachability on this vault to measure
            what the hosts actually accept.
          </p>
        ) : (
          <>
            <p>
              Probed {seg.measured_hosts} of {hosts} host{hosts === 1 ? '' : 's'} from{' '}
              {reach.vantage_points} vantage point{reach.vantage_points === 1 ? '' : 's'}.{' '}
              {seg.responding_hosts} accepted at least one connection.
              {seg.open_ports?.length > 0 && (
                <>
                  {' '}Reachable across the segment:{' '}
                  {seg.open_ports.map((p, i) => (
                    <span key={p.port}>
                      {i > 0 ? ', ' : ''}
                      <span className="font-mono text-slate-700">{p.port}/tcp</span> on {p.hosts} host{p.hosts === 1 ? '' : 's'}
                    </span>
                  ))}
                  .
                </>
              )}
            </p>
            {seg.disputed_hosts > 0 ? (
              <p className="text-amber-700">
                {seg.disputed_hosts} host{seg.disputed_hosts === 1 ? '' : 's'} answered one beacon
                and refused another, so something is filtering by source address. Inside this
                segment that can only be the host itself. From a beacon in another segment it may
                also be the path in between.
              </p>
            ) : reach.vantage_points < 2 ? (
              <p className="text-slate-500">
                Measured from a single vantage point, so this says what is open to that beacon and
                not whether other hosts are treated differently. A second beacon in this vault is
                what surfaces per-source firewall rules.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function Legend({ c, l }) {
  return <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c }} /> {l}</span>;
}

function LegendLine({ c, dash, l }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg width="14" height="4"><line x1="0" y1="2" x2="14" y2="2" stroke={c} strokeWidth="1.4" strokeDasharray={dash ? '2 3' : undefined} /></svg>
      {l}
    </span>
  );
}
